import React from 'react';

import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import dayjs from 'dayjs';

import { Button } from '../../../components/DetailsModal';
import { EventState } from '../../../components/EventCell/types';
import { BaseTutorial } from '../../../services/students/types';
import { Tutorial } from '../../../services/tutorials/types';
import { getStartDatetime } from '../../../services/tutorials/utils';
import addDays from '../../../utils/common/addDays';

export enum TutorialUserType {
  Student = 'student',
  Attendee = 'attendee',
}

export const getStudentTutorialState = (tutorial: Tutorial | BaseTutorial, user: TutorialUserType,
  bookButton: Button, unbookButton: Button, joinButton: Button): EventState => {
  const startDatetime = getStartDatetime(tutorial.startDatetimeIdentifier);

  const now = dayjs();

  const imminentState: EventState = {
    state: 'imminent',
    buttons: [
      {
        ...unbookButton,
        disabled: true,
      },
      joinButton,
    ],
    status: {
      text: 'Live',
      color: 'error',
      icon: <RadioButtonCheckedIcon fontSize="small" />,
    },
  };

  const openState: EventState = {
    state: 'open',
    buttons: [
      bookButton,
    ],
    status: {
      text: 'Available',
      color: 'darkPrimary',
      icon: <GroupIcon fontSize="small" />,
    },
  };

  const bookedState: EventState = {
    state: 'booked',
    buttons: [
      {
        ...unbookButton,
        // Prevent unbook if the tutorial start time is past or within 24 hours
        disabled: now.isAfter(startDatetime.subtract(24, 'hour')),
      },
      {
        ...joinButton,
        disabled: true,
      },
    ],
    status: {
      // If the tutorial is more than 5 minute past finished, display as 'Finished'
      text: now.isAfter(startDatetime.add(tutorial.duration + 5, 'minute')) ? 'Finished' : 'Booked',
      color: 'success',
      icon: <TaskAltIcon fontSize="small" />,
    },
  };

  const fullState: EventState = {
    state: 'full',
    buttons: [
      {
        label: 'Full',
        disabled: true,
        fn: () => {},
      },
    ],
    status: {
      text: 'Full',
      color: 'gray',
      icon: <PersonOffIcon fontSize="small" />,
    },
  };

  const completedState: EventState = {
    state: 'completed',
    status: {
      text: 'Completed',
      color: 'gray',
      icon: <TaskAltIcon fontSize="small" />,
    },
  };

  const readOnlyState: EventState = {
    state: 'readOnly',
    status: {
      text: 'Locked',
      color: 'gray',
      icon: <LockIcon fontSize="small" />,
    },
  };

  // If the user is an attendee
  if (user === TutorialUserType.Attendee) {
    // If tutorial is 5 mins past finished
    if (now.isAfter(startDatetime.add(tutorial.duration + 5, 'minute'))) {
      return completedState;
    }
    // If tutorial is 5 mins before starting
    if (now.isAfter(startDatetime.subtract(5, 'minute'))) {
      return imminentState;
    }
    return bookedState;
  }

  // Checking that this is not a BaseTutorial type which does not have attendees or capacity
  // This is returned by the tutorials stored under the student on Dashboard
  if ('attendees' in tutorial && 'capacity' in tutorial) {
  // If tutorial is too soon to be booked
    if ((now.isAfter(addDays(startDatetime, -1)) && tutorial.attendees?.length === 0)
    || now.isAfter(startDatetime)) {
      return readOnlyState;
    }
    if (tutorial.attendees?.length >= tutorial.capacity) {
      return fullState;
    }
    return openState;
  }

  return readOnlyState;
};
